import { useCallback, useMemo } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { LoadBoundary, NotConnected, Panel, useHubLoad } from "@/modules/hub-kit/HubKit";
import { formatCount } from "@/modules/hub-kit/format";
import { createFinanceOverviewApi } from "../overviewApi";
import { CommissionRulesQueuePage } from "../pages/FinanceQueuePages";

/**
 * Fee and commission configuration.
 *
 * Two things live here and they are NOT the same thing today:
 *  1. The rule the payment path actually applies (a Worker setting,
 *     PLATFORM_FEE_RULE). HQ cannot read a Worker secret, so this tab reports
 *     its EFFECT from real rows: what the payment snapshots recorded.
 *  2. Commission rule records: governed drafts / approvals in the database,
 *     which the payment path does not read yet.
 */
export function FinanceFeesTab() {
  const client = useApiClient();
  const api = useMemo(() => createFinanceOverviewApi(client), [client]);
  const load = useCallback(async () => {
    const to = new Date();
    const from = new Date(to.getTime() - 90 * 86400000);
    return (await api.getOverview(from.toISOString(), to.toISOString())).overview;
  }, [api]);
  const { data, error, loading, reload } = useHubLoad(load);

  return (
    <Stack gap={24}>
      <LoadBoundary
        loading={loading}
        error={error}
        onRetry={() => void reload()}
        hasData={!!data}
        what="the fee status"
      >
        {data ? (
          <Panel
            title="Platform fee at checkout"
            description="What the payment path recorded for captured booking payments in the last 90 days."
          >
            {data.fee.computed_count + data.fee.unset_count + data.fee.unknown_count === 0 ? (
              <Text role="body" color="secondary" as="p">
                No payment has been captured yet, so there is nothing to report. The first captured payment records whether a platform fee was applied.
              </Text>
            ) : (
              <Stack gap={8}>
                <Text role="body" as="p">
                  {formatCount(data.fee.computed_count)} recorded with a platform fee ·{" "}
                  {formatCount(data.fee.unset_count)} recorded gross-only (no fee rule){" "}
                  {data.fee.unknown_count > 0 ? `· ${formatCount(data.fee.unknown_count)} without a fee snapshot` : ""}
                </Text>
                {data.fee.unset_count > 0 ? (
                  <Alert tone="warning" title="No platform fee is being taken">
                    Payments recorded gross-only collect nothing for HUDDL: platform revenue stays at zero and every payment is collected into HUDDL's own Xendit account until a fee rule is configured.
                  </Alert>
                ) : null}
              </Stack>
            )}
          </Panel>
        ) : null}
      </LoadBoundary>

      <NotConnected
        title="Setting the fee is a decision, and it is not in this screen yet"
        missing="The fee applied at payment time is one Worker setting (PLATFORM_FEE_RULE) that has to name the percentage, any fixed amount, who bears Xendit's processing fee and the Xendit split rule. It is deliberately unset until those are decided, and a half-filled rule makes payments refuse to start."
        needs={[
          "Decide the rate and basis (percent of gross, fixed, or both, and any minimum).",
          "Decide who bears Xendit's processing fee on an ordinary booking.",
          "Create the xenPlatform split rule in Xendit and give its id.",
          "Set PLATFORM_FEE_RULE on the API Worker (a secret, so it can only be set from the Cloudflare dashboard).",
        ]}
        recommendation="Keep the fee in one place. Once the rate is decided, make the commission rule below the source of truth and have the payment path read the active rule, so changing the fee is an approved, audited HQ action instead of a Worker deploy."
      />

      <Panel
        title="Commission rules"
        description="Draft, approve, schedule and activate a rule. Activating needs Super Admin approval and replaces the prior active rule. These records are the governance trail; the payment path does not read them yet (see above)."
      >
        <CommissionRulesQueuePage />
      </Panel>
    </Stack>
  );
}
